/*! *********************************************************************
 *
 * Copyright 2021 Adobe
 * All Rights Reserved.
 *
 * NOTICE: All information contained herein is, and remains
 * the property of Adobe and its suppliers, if any. The intellectual
 * and technical concepts contained herein are proprietary to Adobe
 * and its suppliers and are protected by all applicable intellectual
 * property laws, including trade secret and copyright laws.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe.
 *************************************************************************
 */

import { SubstanceMaterial } from './SubstanceMaterial';
import { DownloadAttachment } from './RawAsset';

import _map from 'lodash/map';
import _get from 'lodash/get';
import _find from 'lodash/find';

enum SubstanceIblFormats {
  STAGE = 'stage',
  SBSAR = 'sbsar',
  EXR = 'exr'
}

const SubstanceIblDefaultFormat = SubstanceIblFormats.STAGE;

class SubstanceIbl extends SubstanceMaterial {
  isIbl: boolean;
  _backplates: DownloadAttachment[] | null;

  constructor(rawAsset: Object) {
    super(rawAsset);
    this.isMaterial = false;
    this.formats = [
      SubstanceIblFormats.STAGE,
      SubstanceIblFormats.SBSAR,
      SubstanceIblFormats.EXR
    ];
    this.defaultFormat = SubstanceIblDefaultFormat;
    this.isIbl = true;
    this._backplates = null;
  }

  /**
   * Override the preview that is use on the homepage
   */
  get homePreview() {
    return this.flatPreview || this.mainPreview;
  }

  get previews() {
    const flatPreview =
      this.findAttachments({
        __typename: 'PreviewAttachment',
        tag: 'flat'
      }) || [];
    const backplates =
      this.findAttachments({
        __typename: 'PreviewAttachment',
        tag: 'backplate'
      }) || [];
    let result = [...flatPreview, ...backplates];
    return result;
  }

  get flatPreview() {
    return this.findAttachment({
      __typename: 'PreviewAttachment',
      tag: 'flat'
    });
  }

  get backplates(): DownloadAttachment[] {
    if (this._backplates !== null) return this._backplates;

    const downloadBackplates = this.findAttachments({
      __typename: 'DownloadAttachment',
      tag: 'backplate'
    });
    const previewBackplates = this.findAttachments({
      __typename: 'PreviewAttachment',
      tag: 'backplate'
    });

    const updatedBackplates = _map(downloadBackplates, backplate => ({
      ...backplate,
      previewUrl: _get(
        _find(previewBackplates, { label: backplate.label }),
        'url'
      )
    }));

    this._backplates = updatedBackplates as DownloadAttachment[];

    return this._backplates;
  }
}

export { SubstanceIbl, SubstanceIblFormats, SubstanceIblDefaultFormat };
